import React, { useCallback, useEffect, useState } from "react"
import { EarningCategory } from "../types/wallet/WalletTypes";
import AddPayoutAccount from "../components/wallet/AddPayoutAccount";
import { Link } from "react-router-dom";
import Tabs, { TabButton } from "../components/tabs/Tabs";
import Payout from "../components/wallet/Payout";
import WalletSkeleton from "../skeletons/WalletSkeleton";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../redux-stores/store";
import { fetchWalletData } from "../redux-stores/slices/walletSlice";
import { useSelector } from "react-redux";
import LinkPANModal from "../components/wallet/LinkPANModal";
import { fetchPANDetailsThunk } from "../redux-stores/slices/influencerSlice";

const Wallet: React.FC = () => {

    const dispatch: AppDispatch = useDispatch();

    const walletData = useSelector((state: RootState) => state.wallet);
    const panDetails = useSelector((state: RootState) => state.influencer.pan);

    const [canWithdraw] = useState<boolean>(true);
    const [amount, setAmount] = useState<number>(0);

    const [payoutAccountModalVisible, setPayoutAccountModalVisibility] = useState<boolean>(false);
    const [payoutModalVisible, setPayoutModalVisible] = useState<boolean>(false);
    const [linkPANModalVisible, setLinkPANModalVisiblity] = useState<boolean>(false);

    const [isPayoutProcessing, setPayoutProcessing] = useState<boolean>(false);

    const getWalletData = useCallback(async (force = false) => {
        dispatch(fetchWalletData(force));
    }, [dispatch]);

    useEffect(() => {
        setAmount(walletData.statistics?.balance ?? 0);
    }, [walletData])

    const withdraw = async () => {
        if (amount < 1) return;
        setPayoutModalVisible(true);
    }

    const addPayoutAccount = () => {
        setPayoutAccountModalVisibility(true);
    }

    useEffect(() => {
        if(!walletData.pan.available && walletData.pan.required){
            dispatch(fetchPANDetailsThunk());
        }
    }, [walletData.pan])


    const getTransactionTabs = (): TabButton[] => {
        let buttons: TabButton[] = [
            {
                id: 'earnings',
                content: "Credited"
            },
            {
                id: 'withdrawals',
                content: "Withdrawals"
            }
        ];


        return buttons
    }

    useEffect(() => {
        getWalletData();
    }, [getWalletData])

    if (walletData.loading) return <WalletSkeleton />

    return (
        <React.Fragment>
            <div className="card card-style">
                <div className="card-body d-flex flex-column">
                    <h6>Available balance to withdraw</h6>
                    <span className="color-green-dark fw-bolder font-30 mt-2 mb-3">₹{walletData?.statistics?.balance}</span>
                    {
                        walletData?.payout_account && canWithdraw &&
                        <>
                            {
                                (!walletData.pan.available && walletData.pan.required) ? (
                                    <button className="btn btn-fill btn-sm btn-primary rounded-sm" onClick={() => setLinkPANModalVisiblity(true)}>Add PAN</button>
                                ) : (
                                    <button className="btn btn-fill btn-sm btn-primary rounded-sm" disabled={amount < 1} onClick={withdraw}>Withdraw</button>
                                )
                            }
                        </>
                    }
                    {
                        !walletData?.payout_account && canWithdraw &&
                        <button className="btn btn-fill btn-sm btn-primary rounded-sm" onClick={addPayoutAccount}>Add Bank Details to withdraw</button>
                    }
                </div>
            </div>
            <div className="card card-style">
                <div className="card-body d-flex flex-column">
                    <h6>Total Money Earned</h6>
                    <span className="pop fw-bolder font-30 mt-2 mb-3">₹{walletData?.statistics?.earnings}</span>
                    {
                        walletData?.statistics?.category_earnings &&
                        Object.entries(walletData.statistics?.category_earnings).map(([key, categoryEarning], i) => (
                            <div key={i} className="card border-gray-400 rounded-4 bg-gray-200 shadow-none mb-2">
                                <div className="card-body d-flex flex-column p-3">
                                    <span className="text-dark fw-bolder font-18">₹{categoryEarning.earnings}</span>
                                    <div className="d-flex">
                                        <span className="text-gray-600">{categoryEarning.name} Earnings</span>
                                        {key === EarningCategory.REFERRAL && <Link className="fw-bold ms-auto" to="/referral">Refer Now</Link>}
                                    </div>
                                </div>
                            </div>
                        ))
                    }
                </div>
            </div>
            <div>
                <h6 className="pop mx-3">Transaction History</h6>
                {
                    (walletData?.transactions.length === 0) &&
                    <div className="card card-style mt-3">
                        <div className="card-body">
                            <div className="d-flex flex-column align-items-center p-4">
                                <h3 className="mb-3 pop">Collaborate with Top Brands and Start Earning Today</h3>
                                <p className="mb-3">Partner with leading brands to turn your creativity into income. Build meaningful collaborations and grow your earning opportunities effortlessly </p>
                                <Link className="btn btn-sm rounded-sm btn-primary" to="/">Explore Sponsorships</Link>
                            </div>
                        </div>
                    </div>
                }
                {
                    walletData?.transactions && walletData?.transactions.length > 0 &&
                    <Tabs buttons={getTransactionTabs()} buttonClass="m-3">
                        <div className="card card-style mt-3">
                            <div className="card-body p-1">
                                <div className="table-responsive">
                                    <table className="table">
                                        <tbody>
                                            {
                                                walletData?.transactions.map((transaction, i) => {
                                                    return (
                                                        <tr key={i} className={i === walletData?.transactions.length - 1 ? " " : "border-bottom border-gray-200"} style={{ border: walletData?.transactions.length - 1 === i ? 'transparent' : '' }}>
                                                            <td>
                                                                <span className="text-gray-500 d-block fs-7">{transaction.posted}</span>
                                                                <span className="text-dark">{transaction.narration}</span>
                                                            </td>
                                                            <td className="text-end fw-bold align-middle">
                                                                {
                                                                    transaction.dr_amount > 0 &&
                                                                    <span className="text-danger">(-) ₹{transaction.dr_amount}</span>
                                                                }
                                                                {
                                                                    transaction.cr_amount > 0 &&
                                                                    <span className="text-success">₹{transaction.cr_amount}</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="card card-style mt-3">
                            <div className="card-body p-1">
                                <div className="table-responsive">
                                    <table className="table">
                                        <tbody>
                                            {
                                                walletData?.payouts.map((payout, i) => {
                                                    return (
                                                        <tr key={i} className={i === walletData?.payouts.length - 1 ? " " : "border-bottom border-gray-200"} style={{ border: walletData?.payouts.length - 1 === i ? 'transparent' : '' }}>
                                                            <td>
                                                                <span className="text-gray-500 d-block fs-7">{payout.posted}</span>
                                                                {
                                                                    payout.status === 'processed' &&
                                                                    <span className="d-block text-success">Payout Successful</span>
                                                                }
                                                                {
                                                                    payout.status === 'queued' &&
                                                                    <span className="d-block text-success">Payout Processing</span>
                                                                }
                                                                {
                                                                    payout.status === 'failed' &&
                                                                    <span className="d-block text-success">Payout Failed</span>
                                                                }
                                                            </td>
                                                            <td className="text-end fw-bold align-middle">
                                                                {
                                                                    payout.status === 'processed' &&
                                                                    <span className="text-success">₹{payout.amount}</span>
                                                                }
                                                                {
                                                                    payout.status === 'queued' &&
                                                                    <span className="text-success">₹{payout.amount}</span>
                                                                }
                                                                {
                                                                    payout.status === 'failed' &&
                                                                    <span className="text-success">₹{payout.amount}</span>
                                                                }
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                            }
                                            {
                                                walletData?.payouts.length === 0 &&
                                                <tr className="text-center border-0 mt-2">
                                                    <td colSpan={3} className="border-0 pt-4">No records found.</td>
                                                </tr>
                                            }
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </Tabs>
                }
            </div>
            <AddPayoutAccount
                isActive={payoutAccountModalVisible}
                onClose={() => setPayoutAccountModalVisibility(false)}
                onSuccess={() => {
                    getWalletData(true)
                    setPayoutAccountModalVisibility(false);
                }}
            />
            <LinkPANModal 
                isActive={linkPANModalVisible}
                onClose={() => setLinkPANModalVisiblity(false)}
            />
            {
                amount > 0 &&
                <Payout
                    isActive={payoutModalVisible}
                    onClose={() => {
                        setPayoutModalVisible(false)
                    }}
                    onConfirmation={(is_processing) => setPayoutProcessing(is_processing)}
                    bankAccountNumber={walletData?.payout_account?.bank_account_number ?? ''}
                    amount={walletData?.statistics?.balance!}
                    disableBackdropClick={isPayoutProcessing}
                />
            }
        </React.Fragment>

    )
}

export default Wallet;